import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';

import { ClientApiService } from 'services/ClientApiService';
import { Button, ButtonVariant, ButtonType, ButtonSize } from 'components/Button';
import { Modal } from 'components/Modal';
import { Icon } from 'components/tokens/Icon';
import { Colors } from 'enums/Colors';
import { arrowRight, informationCircle } from 'icons/default';
import { getDataLayerEvents } from 'utils/getDatalayerEvents';

import styles from './ReleaseCalculator.module.scss';

interface Props {
  partner: string;
}

export const ReleaseCalculator: React.FC<Props> = ({ partner }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<any>();

  const [responseFromApi, setResponseFromApi] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const [isLoading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [residualAmount, setResidualAmount] = useState<any | null>('');

  const [showLicenseplate, setShowLicenseplate] = useState(true);

  const [showResidualForm, setShowResidualForm] = useState(false);
  const [showCalcualtion, setShowCalcualtion] = useState(false);

  const [step, setStep] = useState('1');

  const [typeFormResidual, setTypeFormResidual] = useState<number>();

  const [showContactFrom, setShowContactForm] = useState(false);

  const [success, setSuccess] = useState(false);

  const [generalError, setGeneralError] = useState(false);

  function numberWithSpaces(x: { toString: () => string }) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }
  const onSubmit: SubmitHandler<any> = async (data) => {
    const dataToSend = Object.assign(data, {
      residual: typeFormResidual,
      license_plate: responseFromApi?.license_plate,
      partner,
    });
    // console.log(dataToSend);

    try {
      setLoading(true);

      const method = 'postRelease';
      const payload = dataToSend;
      const response = await ClientApiService.proxyRequest({ method, payload });
      const responseJson = await response.json();
      // console.log(dataToSend);
      if (!response.ok) {
        console.log(responseJson);
        setLoading(false);
        setShowContactForm(false);
        setGeneralError(true);
      } else {
        //  console.log(responseJson);
        setLoading(false);
        setShowContactForm(false);
        setSuccess(true);

        getDataLayerEvents('release-application-submitted');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleContactMeButton = () => {
    setShowLicenseplate(false);
    setShowResidualForm(false);
    setShowContactForm(true);
  };

  const handleShowContactForm = () => {
    setShowContactForm(true);
    setShowCalcualtion(false);
    setStep('4');
  };

  const restetForm = () => {
    setShowCalcualtion(false);
    setShowResidualForm(true);
    setResidualAmount(null);
    setErrorMessage('');
    setShowContactForm(false);
    setStep('2');
  };

  function calculateLoanMonthlyCost(
    rate = 0,
    periods = 0,
    present_value = 0,
    future_value = 0,
    beginning = 0
  ) {
    // eslint-disable-next-line
    {
      const when = beginning ? 1 : 0;

      if (rate === 0) {
        return -(future_value + present_value) / periods;
      }

      return (
        // eslint-disable-next-line
        -(future_value + present_value * Math.pow(1 + rate, periods)) /
        // eslint-disable-next-line
        (((1 + rate * when) / rate) * (Math.pow(1 + rate, periods) - 1))
      );
    }
  }

  const formatResidualAmount = (value: string) => {
    // Ta bort eventuella mellanrum om de redan finns i värdet
    const cleanValue = value.replace(/\s/g, '');

    // Konvertera till ett numeriskt värde
    const numericValue = parseFloat(cleanValue);

    // Kontrollera om det är ett giltigt numeriskt värde
    if (!Number.isNaN(numericValue)) {
      // Formatera värdet med tusentalsavgränsare och returnera det
      setTypeFormResidual(numericValue);
      return numericValue.toLocaleString('sv-SE');
    }
    // Om värdet inte är giltigt, returnera det ursprungliga värdet
    return value;
  };

  const handleInputChange = (e: { target: { value: string } }) => {
    // Anropa formatResidualAmount för att formatera värdet innan det sparas i state
    const formattedValue = formatResidualAmount(e.target.value);
    setResidualAmount(formattedValue);
  };

  const handleSubmitLicenseplate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query) {
      return;
    }

    setLoading(true);

    try {
      const response = await ClientApiService.checkCar(query, '', 'release');
      if (response.ok) {
        const responseJson = await response.json();
        // console.log('API-svar:', responseJson); // Logga hela svaret för inspektion

        if (
          responseJson.response.status_code === 429 ||
          responseJson.response.status_code === 500
        ) {
          // Om statuskoden är 200, visa felmeddelandet för användaren
          const error = responseJson.response.errors[0];
          // console.log('API-felmeddelande 2:', error);

          if (error === 'Could not find a car with this license plate.') {
            setErrorMessage('Vi kunde inte hitta någon bil med det registreringsnumret.');
          } else {
            setErrorMessage(error);
          }

          setLoading(false);
        } else {
          const data = responseJson.response.payload;
          setResponseFromApi(data);
          setLoading(false);
          setShowLicenseplate(false);
          setShowResidualForm(false);
          setErrorMessage('');
          setStep('2');
          setShowContactForm(true);
          // console.log(data);
        }
      }
    } catch (error) {
      setLoading(false);

      setErrorMessage('Ett fel inträffade:');

      // set error to vi kunde inte hitta bilen.
    }
  };

  const handleSubmitResidual = (e: React.FormEvent) => {
    e.preventDefault(); // Förhindra formulärskickning

    // Konvertera residualAmount till ett numeriskt värde
    const residualAmountNumeric = parseFloat(residualAmount.replace(/\s/g, ''));

    if (!Number.isNaN(residualAmountNumeric)) {
      const monthlyCost = calculateLoanMonthlyCost(
        9.2 / 100 / 12, // monthly
        36, // term
        -residualAmountNumeric * 0.8,
        residualAmountNumeric * 0.8 * 0.55, // Residual value
        1
      );
      setResidualAmount(monthlyCost);

      setShowResidualForm(false);
      setShowCalcualtion(true);
      setStep('3');
    }
    // Om residualAmount inte är ett giltigt numeriskt värde, hantera felet här

    setErrorMessage('Ogiltig restskuld');
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.steps}>Steg {step} av 4</div>
      {!success && !generalError && <h2>Bli kontaktad av en leasingexpert</h2>}

      <p className={styles.paragraph}>
        {!responseFromApi && !showContactFrom && !success
          ? 'Börja med att fylla i registreringsnumret'
          : ''}
      </p>
      {isLoading ? (
        <div className={styles.loader} />
      ) : (
        <div>
          {responseFromApi && !success && !showContactFrom && (
            <div className="section-1">
              <p>
                {responseFromApi.license_plate} | {responseFromApi.brand} {responseFromApi.model}{' '}
                {responseFromApi.model_year}
              </p>
            </div>
          )}
        </div>
      )}

      {showLicenseplate && (
        <form onSubmit={handleSubmitLicenseplate}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            pattern="^[A-Za-z]{3}\d{2}[A-Za-z\d]$"
            title="Vänligen fyll i ett korrekt registreringsnummer"
            required
            placeholder="ABC123"
            className={styles.licenseplate}
            maxLength={6}
          />

          <Button
            size={ButtonSize.Small}
            type={ButtonType.Submit}
            variant={ButtonVariant.PrimaryMintSweet}
            stretch
          >
            Hämta biluppgifter
          </Button>
          {/*
          <Button
            size={ButtonSize.Small}
            type={ButtonType.Button}
            variant={ButtonVariant.GhostInverted}
            stretch
            onClick={handleContactMeButton}
          >
            Jag vill bli kontaktad
          </Button>
           */}
          {errorMessage && <p className={styles.error}>{errorMessage}</p>}
        </form>
      )}

      {showResidualForm && (
        <form onSubmit={handleSubmitResidual}>
          <div className={styles.extraControls}>
            <p className={styles.paragraph}>Vad är din aktuella restskuld (inkl. moms)?</p>
            <Modal ButtonContent={<Icon color={Colors.White} icon={informationCircle} />}>
              <h4>Aktuell restskuld</h4>
              <p>
                Restskuld är den summa som du har kvar att betala tillbaka till finansbolaget när
                leasingperioden är slut.
              </p>
              <p>
                För att räkna fram ett nytt erbjudande behöver vi din aktuella restskuld för din
                leasing.
              </p>

              <p>Kontakta ditt nuvarande finansieringsbolag och begär ut restskulden.</p>
              <p>
                Behöver du hjälp kan du alltid kontakta oss på: <br />
                <a href="tel:010-1463730">010-1463730</a>
              </p>
            </Modal>
          </div>
          <input
            type="text"
            onChange={handleInputChange}
            value={residualAmount}
            title="Vänligen fyll i din aktuella restskuld"
            required
            placeholder="Aktuell restskuld"
          />
          {errorMessage && <p className={styles.error}>{errorMessage}</p>}
          <Button
            size={ButtonSize.Small}
            type={ButtonType.Submit}
            variant={ButtonVariant.PrimaryMintSweet}
            stretch
          >
            Beräkna månadskostnad
          </Button>
          <Button
            size={ButtonSize.Small}
            type={ButtonType.Button}
            variant={ButtonVariant.GhostInverted}
            stretch
            onClick={handleContactMeButton}
          >
            Jag vet inte restskuld
          </Button>
        </form>
      )}

      {showCalcualtion && (
        <>
          <div className={styles.calculation}>
            <span>Din nya månadskostnad exkl. moms</span>
            <span className={styles.monthlycost}>
              {numberWithSpaces(parseInt(residualAmount, 10))} kr/mån
            </span>
            <Button
              trailingIcon={arrowRight}
              type={ButtonType.Button}
              variant={ButtonVariant.PrimaryMintSweet}
              stretch
              size={ButtonSize.Small}
              onClick={handleShowContactForm}
              // href={`https://blipp.typeform.com/release#partner=${partner}&residual_value=${typeFormResidual}`}
            >
              <span>Gå vidare</span>
            </Button>
          </div>

          <Button
            size={ButtonSize.Small}
            type={ButtonType.Button}
            variant={ButtonVariant.GhostInverted}
            stretch
            onClick={restetForm}
          >
            Tillbaka
          </Button>
        </>
      )}

      {showContactFrom && (
        <div className={styles.contactWrapper}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <input
              type="text"
              placeholder="Telefonnumer"
              {...register('phone_number', { required: true })}
            />
            {errors.phone_number && <p className={styles.error}>Ange ditt telefonnummer</p>}

            <input type="email" placeholder="E-post" {...register('email', { required: true })} />

            {errors.email && <p className={styles.error}>Ange din e-postadress</p>}

            <Button
              trailingIcon={arrowRight}
              type={ButtonType.Submit}
              variant={ButtonVariant.PrimaryMintSweet}
              stretch
              size={ButtonSize.Small}
              onClick={handleShowContactForm}
              // href={`https://blipp.typeform.com/release#partner=${partner}&residual_value=${typeFormResidual}`}
            >
              <span>Skicka</span>
            </Button>
          </form>
        </div>
      )}

      {success && (
        <div className={styles.successWrapper}>
          <h2>Tack för din ansökan</h2>
          <p>Våra leasingexperter hör av sig inom kort.</p>
        </div>
      )}
      {generalError && (
        <div className={styles.errorWrapper}>
          <h2>Någonting gick fel!</h2>
          <p>Försök igen eller kontakt oss via chatten.</p>
        </div>
      )}
      <div className={styles.trustpilot}>
        <span>Utmärkt</span>
        <img width="120" src="/assets/icons/logo/Trustpilot_rating.svg" alt="Trustpilot logo" />
        <a href="https://se.trustpilot.com/review/blipp.se" target="_BLANK" rel="noreferrer">
          <img
            width="100"
            src="/assets/icons/logo/Trustpilot_brandmark.svg"
            alt="Trustpilot logo"
          />
        </a>
      </div>
    </div>
  );
};
